/**
 * =============================================================================
 * SCRIPT-NAME:   gsc_status.js
 * PROJEKT:       Freiseinapp - GSC KPI-Dashboard
 * MODUL:         Per-Seite-Statusansicht — wo steht jede LP/LB, Rank & Probleme
 * BESCHREIBUNG:  Liest gsc_get_status.php (Index-Status + Tages-Rank + belastbare
 *                Problem-Flags) und zeigt JE SEITE EIN PANEL, Probleme zuerst.
 * VERSION:       v2.0 · STAND: 2026-10-21 21:55 Europe/Berlin
 * AENDERUNG:     Tabelle -> Panels (Georg 2026-10-21: "Jede Seite ist ein Panel wie
 *                in GADS KPI Performance Dashboard die Bxx Panels"). Drei Ebenen:
 *                Kopf mit den Kennzahlen -> Wochendaten -> Lebenszyklus auf weisser
 *                Flaeche. Dazu eine Suche nach der Seite und die Sortierleiste
 *                anstelle des Tabellenkopfs.
 *                ⚠ Die WOCHENDATEN sind noch leer: gsc_get_status.php liefert je
 *                  Seite spark, hist, crawls und notes - keine Wochenwerte. Der
 *                  Platz steht, die Ebene stimmt, die Daten fehlen noch.
 * =============================================================================
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Link } from "react-router-dom";
import "./gsc_status.css";

const API_BASE = "https://freiseinapp.de/api";
const PILLAR_COLORS = { VA:'#dc2626', EF:'#0ea5e9', EA:'#16a34a', SO:'#9333ea', PBC:'#d97706' };

// Letzter Crawl als Zahl. Seiten ohne erfassten Crawl gelten als "unendlich lange
// nicht gesehen" und stehen deshalb bei "älteste zuerst" ganz oben — das ist der
// Extremfall der Frage, nicht ihr Ausreißer.
const crawlZeit = p => {
    const c = (p.crawls || [])[0];
    return c ? new Date(String(c.crawl_time).replace(' ', 'T')).getTime() : -Infinity;
};

// Sortier-Definition je Spalte. `erst` ist die Richtung des ersten Klicks —
// jeweils die, die das Interessante nach oben holt.
const SPALTEN = {
    prefix: { label: 'Seite',     wert: p => p.prefix || '',                              erst: 'asc'  },
    spark:  { label: 'Impr. 30T', wert: p => (p.spark || []).reduce((a, b) => a + b, 0),  erst: 'desc' },
    pillar: { label: 'Pillar',    wert: p => p.pillar || '',                              erst: 'asc'  },
    index:  { label: 'Index',     wert: p => (p.is_indexed === null ? -1 : p.is_indexed), erst: 'asc'  },
    crawl:  { label: 'Crawl',     wert: crawlZeit,                                        erst: 'asc'  },
    rank:   { label: 'Rank',      wert: p => (p.rank === null ? Infinity : p.rank),       erst: 'asc'  },
    impr:   { label: 'Impr',      wert: p => p.impressions || 0,                          erst: 'desc' },
    klicks: { label: 'Klicks',    wert: p => p.clicks || 0,                               erst: 'desc' },
    // Fällige Prüfungen zuerst, dann offene, dann die Zahl der Einträge — eine Seite
    // mit einer fälligen Prognose ist dringender als eine mit zehn erledigten.
    hist:   { label: 'Lebenslauf', wert: p => {
                  const h = p.hist || [];
                  return h.filter(x => x.faellig).length * 10000
                       + h.filter(x => x.eingetreten === null).length * 100 + h.length;
              }, erst: 'desc' },
    notes:  { label: 'Notizen',   wert: p => (p.notes || []).length,                      erst: 'desc' },
};

function IndexBadge({ p }) {
    if (p.is_indexed === 1) return <span className="gs-badge gs-b-ok">✅ indexiert</span>;
    if (p.is_indexed === null) return <span className="gs-badge gs-b-warn">– n/a</span>;
    const unknown = (p.coverage_state || '').includes('nicht bekannt');
    return <span className="gs-badge gs-b-crit">{unknown ? '🔴 unbekannt' : '🔴 nicht indexiert'}</span>;
}

// Mini-Sparkline: Impressionen je Tag der ROLLIERENDEN letzten 30 Tage (values[0]=ältester Tag).
function Sparkline({ values }) {
    const W = 96, H = 24, P = 2;
    if (!Array.isArray(values) || values.length === 0) return <span style={{ color: '#c4c4c4' }}>–</span>;
    const total = values.reduce((a, b) => a + b, 0);
    if (total === 0) return <span style={{ color: '#c4c4c4' }} title="0 Impressionen in 30 Tagen">–</span>;
    const peak = Math.max(...values);
    const n = values.length;
    const dx = n > 1 ? (W - 2 * P) / (n - 1) : 0;
    const yOf = v => H - P - (peak > 0 ? (v / peak) * (H - 2 * P) : 0);
    const pts = values.map((v, i) => `${(P + i * dx).toFixed(1)},${yOf(v).toFixed(1)}`).join(' ');
    const li = n - 1;
    return (
        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ display: 'block' }}
             role="img" aria-label={`Impressionen 30 Tage: Summe ${total}, Spitze ${peak}`}>
            <title>{`Impressionen letzte 30 Tage — Summe ${total}, Spitze ${peak}/Tag`}</title>
            <polyline points={pts} fill="none" stroke="#1a7f5a" strokeWidth="1.5" />
            <circle cx={(P + li * dx).toFixed(1)} cy={yOf(values[li]).toFixed(1)} r="2" fill="#1a7f5a" />
        </svg>
    );
}

// "2026-07-09 10:15:32" → "09.07. 10:15"
function fmtTs(ts) {
    if (!ts) return '';
    const [d, t] = String(ts).split(' ');
    if (!d) return String(ts);
    const [, M, D] = d.split('-');
    return `${D}.${M}. ${(t || '').slice(0, 5)}`;
}

// Datum ohne Uhrzeit: "21.08."
function fmtTag(ts) {
    if (!ts) return '';
    const [, M, D] = String(ts).split(' ')[0].split('-');
    return `${D}.${M}.`;
}

// Volles Datum mit Uhrzeit: "21.08.2026, 09:16"
function fmtVoll(ts) {
    if (!ts) return '';
    const [d, t] = String(ts).split(' ');
    const [Y, M, D] = d.split('-');
    return `${D}.${M}.${Y}, ${(t || '').slice(0, 5)}`;
}

function tageSeit(ts) {
    if (!ts) return null;
    const then = new Date(String(ts).replace(' ', 'T'));
    return Math.floor((Date.now() - then.getTime()) / 86400000);
}

// Crawl-Verlauf je Seite (gsc_crawl_history, ab 2026-08-22).
// gsc_url_inspection kennt nur den LETZTEN Crawl — hier steht jeder Zeitpunkt.
// Die Alters-Ampel ist der eigentliche Nutzwert: sie zeigt auf einen Blick,
// welche Seiten Google seit Wochen nicht mehr angesehen hat.
function CrawlHistory({ page }) {
    const [open, setOpen] = useState(false);
    const crawls = page.crawls || [];
    const n = page.crawl_count || 0;
    if (!n || !crawls.length) return <span className="gs-crawl-none" title="Noch kein Crawl erfasst">–</span>;

    const letzter = crawls[0];
    const alter = tageSeit(letzter.crawl_time);
    const ampel = alter === null ? '' : alter <= 7 ? 'frisch' : alter <= 30 ? 'mittel' : 'alt';
    // Verweisende Seiten laut Google zum Crawl-Zeitpunkt. 0 ist das eigentliche
    // Signal: eine Seite, auf die nichts zeigt, hat Google keinen Anlass zu gewichten.
    // ⚠ Laut Google eine Stichprobe, keine vollständige Liste.
    const refs = letzter.refs;

    return (
        <div className="gs-crawl">
            <button className={`gs-crawl-btn gs-crawl-${ampel}`} onClick={() => setOpen(!open)}
                title={`${fmtVoll(letzter.crawl_time)} · ${n} erfasst`}>
                <span className="gs-crawl-date">{fmtTag(letzter.crawl_time)}</span>
                {alter !== null && <span className="gs-crawl-age">{alter}T</span>}
                {refs !== null && refs !== undefined && (
                    <span className={`gs-crawl-refs${refs === 0 ? ' gs-crawl-refs-null' : ''}`}
                        title={refs === 0
                            ? 'Keine verweisende Seite — Google kennt keinen internen Link hierher'
                            : `${refs} verweisende Seite${refs === 1 ? '' : 'n'} laut Google (Stichprobe)`}>
                        🔗{refs}
                    </span>
                )}
                {n > 1 && <span className="gs-crawl-n">{n}×</span>}
                <span className="gs-crawl-caret">{open ? '▾' : '▸'}</span>
            </button>
            {open && (
                <ul className="gs-crawl-list">
                    {crawls.map(c => (
                        <li key={c.crawl_time}>
                            <span className="gs-crawl-ts">{fmtVoll(c.crawl_time)}</span>
                            <span className="gs-crawl-as" title={c.crawled_as || 'unbekannt'}>
                                {c.crawled_as === 'MOBILE' ? '📱' : c.crawled_as ? '🖥' : '·'}
                            </span>
                            {c.refs !== null && c.refs !== undefined && (
                                <span className={`gs-crawl-refs${c.refs === 0 ? ' gs-crawl-refs-null' : ''}`}>🔗{c.refs}</span>
                            )}
                            <span className={c.is_indexed ? 'gs-crawl-ok' : 'gs-crawl-bad'}>
                                {c.coverage_state || '–'}
                            </span>
                        </li>
                    ))}
                    {n > crawls.length && (
                        <li className="gs-crawl-more">… {n - crawls.length} ältere nicht angezeigt</li>
                    )}
                </ul>
            )}
        </div>
    );
}

// Notiz-Verlauf je Seite: datierte Einträge (neueste oben) + Eingabebox.
// Bei Problem-Seiten ist die Box mit dem roten Problemtext vorbefüllt (source='problem').
// ── Lebenslauf einer Seite (gsc_history, seit 2026-10-21) ───────────────────
// Eine Zeile je HANDLUNG, mit Prognose und Prüfdatum. Der Unterschied zur Notiz
// daneben: eine Notiz hält fest, was ist — ein Lebenslauf-Eintrag sagt vorher,
// was eintreten soll, und ist am Prüftag entscheidbar.
//
// 🔴 DIE SCHWELLE IST PFLICHT. Das Feld `wirkhand` muss „verfehlt unter <Wert>"
//    enthalten; die Datenbank weist einen Satz ohne sie ab (CHECK). Grund: am
//    2026-10-20 trugen fünf von fünf fälligen Hist-Sätzen keine Schwelle, und
//    das Urteil war damit zweiwertig statt dreiwertig.
function nächsterFreitag() {
    const d = new Date();
    d.setDate(d.getDate() + ((5 - d.getDay() + 7) % 7 || 7));
    return d.toISOString().slice(0, 10);
}

// Ein Feld der aufgeklappten Ansicht. Leere Felder fallen weg, statt als
// leere Zeile zu stehen — `ursache`, `ergebnis` und `schluss` sind bei den
// meisten Einträgen NULL, und eine Reihe leerer Etiketten verdeckt die gefüllten.
function Feld({ k, v, pre }) {
    if (v === null || v === undefined || String(v).trim() === '') return null;
    return (
        <div className="gs-hist-feld">
            <span className="gs-hist-k">{k}</span>
            {pre ? <pre className="gs-hist-pre">{v}</pre> : <span className="gs-hist-v">{v}</span>}
        </div>
    );
}

function Lebenslauf({ page, token, onNeu, startOffen }) {
    const [offen, setOffen] = useState(false);
    // 🔴 `startOffen` kommt aus dem Panel: wer dort zwei Ebenen aufgeklappt hat,
    //    will den Lebenszyklus sehen und nicht noch ein drittes Mal klicken.
    //    In der alten Tabellenzeile blieb er zu, weil er dort Platz fraß.
    const [detail, setDetail] = useState(!!startOffen);
    const [f, setF] = useState({ handlung: '', wirkhand: '', pruef_datum: nächsterFreitag() });
    const [busy, setBusy] = useState(false);
    const [fehler, setFehler] = useState('');
    const hist = page.hist || [];
    const fällig = hist.filter(h => h.faellig).length;
    const offenN = hist.filter(h => h.eingetreten === null).length;

    async function speichern() {
        if (busy) return;
        if (!f.handlung.trim()) { setFehler('Handlung fehlt.'); return; }
        if (!/verfehlt unter/i.test(f.wirkhand)) {
            setFehler('Prognose braucht eine Schwelle: „… verfehlt unter <Wert>"'); return;
        }
        setBusy(true); setFehler('');
        try {
            const res = await fetch(`${API_BASE}/gsc_history.php`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    prefix: page.prefix, url: page.url, handlung: f.handlung.trim(),
                    h_datum: new Date().toISOString().slice(0, 10),
                    wirkhand: f.wirkhand.trim(), pruef_datum: f.pruef_datum,
                    liegt_bei: 'claude', quelle: 'status',
                }),
            });
            const j = await res.json();
            if (j.success) {
                setF({ handlung: '', wirkhand: '', pruef_datum: nächsterFreitag() });
                setOffen(false);
                if (onNeu) onNeu();
            } else { setFehler(j.message || 'Fehler beim Speichern.'); }
        } catch (e) { setFehler(String(e)); }
        setBusy(false);
    }

    return (
        <div className="gs-hist">
            <button className="gs-hist-toggle" onClick={() => setOffen(!offen)}
                title="Handlung mit Prognose erfassen">
                {hist.length === 0 ? '— ' : `${hist.length} `}
                {fällig > 0 && <span className="gs-hist-faellig" title={`${fällig} Prüfung(en) fällig`}>🔴{fällig}</span>}
                {fällig === 0 && offenN > 0 && <span className="gs-hist-offen" title={`${offenN} offen`}>⏳{offenN}</span>}
                <span className="gs-hist-plus">＋</span>
            </button>
            {/* 🔴 NUR DER JUENGSTE EINTRAG in der Zeile (Georg 2026-10-21: "bring das in
                eine Zeile"). Vorher drei - das machte die Tabellenzeile vier Zeilen hoch.
                Die Gesamtzahl steht im Knopf, der volle Text im title. */}
            {hist.length > 0 && (
                <ul className="gs-hist-list">
                    {hist.slice(0, 1).map(h => (
                        <li key={h.id} className={h.faellig ? 'gs-hist-row-faellig' : ''}>
                            <span className="gs-note-ts">{h.h_datum}</span>
                            {/* 🔴 KLICK statt title (Georg 2026-10-21: "das Lebenslauffeld muss
                                scrollbar sein wenn ich reinklicke und mir den gesamten Inhalt
                                anzeigen nicht nur als tool tip"). Ein title verschwindet beim
                                Wegziehen der Maus, lässt sich nicht scrollen und nichts daraus
                                kopieren — bei Vorher/Nachher über mehrere Zeilen trägt er nicht. */}
                            <span className="gs-note-txt gs-note-klick"
                                onClick={() => setDetail(!detail)}
                                title={detail ? 'zuklappen' : 'klicken: ganzer Lebenslauf'}>
                                {h.eingetreten === 1 ? '✅ ' : h.eingetreten === 0 ? '🔴 ' : h.faellig ? '🔴 fällig ' : '⏳ '}
                                {h.handlung}
                                {h.liegt_bei === 'georg' && <em title="liegt bei Georg"> · G</em>}
                                <span className="gs-hist-mehr">{detail ? '▾' : '▸'}</span>
                            </span>
                        </li>
                    ))}
                </ul>
            )}
            {detail && hist.length > 0 && (
                <div className="gs-hist-voll">
                    {hist.map(h => (
                        <div key={h.id} className="gs-hist-eintrag">
                            <div className="gs-hist-ekopf">
                                {h.eingetreten === 1 ? '✅' : h.eingetreten === 0 ? '🔴' : h.faellig ? '🔴 fällig' : '⏳'}
                                {' '}{h.h_datum}
                                {h.arbeit && <span className="gs-hist-arbeit">{h.arbeit}</span>}
                                <span className="gs-hist-pdatum">Prüftag {h.pruef_datum}</span>
                            </div>
                            <Feld k="Handlung" v={h.handlung} />
                            <Feld k="Ursache"  v={h.ursache} />
                            <Feld k="Vorher"   v={h.vorher} pre />
                            <Feld k="Nachher"  v={h.nachher} pre />
                            {/* 🔴 Die Zeit NUR mit ihrem Richtwert (Georg 2026-10-21): langsam
                                heisst 15 % über dem Durchschnitt DESSELBEN Laufs. 0,82 s allein
                                sagt nichts, 0,82 gegen 0,95 sagt alles. */}
                            {h.zeit_a1 != null && (
                                <Feld k="Zeit a1" v={
                                    `${h.zeit_a1.toFixed(2)} s` +
                                    (h.zeit_richtwert != null
                                        ? ` · Richtwert ${h.zeit_richtwert.toFixed(2)} s · ${
                                            Math.round((h.zeit_a1 / h.zeit_richtwert - 1) * 100) > 0 ? '+' : ''
                                          }${Math.round((h.zeit_a1 / h.zeit_richtwert - 1) * 100)} %`
                                        : ' · ohne Richtwert nicht deutbar')
                                } />
                            )}
                            <Feld k="Prognose" v={h.wirkhand} />
                            <Feld k="Ergebnis" v={h.ergebnis} />
                            <Feld k="Schluss"  v={h.schluss} />
                            {/* Pfad des Pruefberichts im Repo (Georg 2026-10-26: "Der Link
                                zum Prüfbericht gehört dort rein"). Leer faellt die Zeile weg. */}
                            <Feld k="Bericht"  v={h.bericht} />
                        </div>
                    ))}
                </div>
            )}
            {offen && (
                <div className="gs-hist-form">
                    <input className="gs-hist-in" placeholder="Handlung — was wurde getan?"
                        value={f.handlung} onChange={e => setF({ ...f, handlung: e.target.value })} />
                    <input className="gs-hist-in" placeholder="Prognose — von X auf Y, verfehlt unter Z"
                        value={f.wirkhand} onChange={e => setF({ ...f, wirkhand: e.target.value })} />
                    <input className="gs-hist-in" type="date" value={f.pruef_datum}
                        title="Prüftag — Vorgabe ist der nächste Freitag"
                        onChange={e => setF({ ...f, pruef_datum: e.target.value })} />
                    {fehler && <div className="gs-hist-fehler">{fehler}</div>}
                    <button className="gs-notes-add" onClick={speichern} disabled={busy}>speichern</button>
                </div>
            )}
        </div>
    );
}

/**
 * Die WOCHENDATEN einer Seite — gebaut wie B01 im GAds-KPI-Dashboard
 * (Georg 2026-10-21: "die Wochendaten wie bei gkpd", "nur hier auf die
 * einzelne seite bezogen").
 *
 * 🔴 DIE PROZESS-SPALTE IST DIE BRUECKE ZUM LEBENSZYKLUS. Ein Hist-Eintrag
 *    steht in der Woche, in die sein `h_datum` faellt - so sieht man die Zahlen
 *    einer Woche und daneben, was in dieser Woche an der Seite getan wurde.
 *    Ein Klick auf den Chip oeffnet den vollen Satz darunter.
 *
 * ⚠ Eintraege, deren Woche nicht in den Daten vorkommt, gehen nicht verloren:
 *   sie stehen unter der Tabelle als "ausserhalb des Zeitraums". Sonst waere
 *   eine Handlung unsichtbar, nur weil die Seite in jener Woche nichts hatte.
 */
// Freitag und Donnerstag der KPI-Woche eines Datums. 🔴 Dieselbe Rechnung wie
// im Endpunkt: date - ((WEEKDAY(date)+3) MOD 7) Tage - die Woche beginnt am
// FREITAG (Georg 2026-10-25: "Abgemacht ist aber Freitag bis Donnerstag. Weil
// dann am Freitag wieder KPI Tag ist. Das gilt fuer GSC und fuer GADS").
// Bis dahin ISO-Woche ab Montag.
//
// 🔴 KEIN toISOString() FÜR EIN KALENDERDATUM. Es rechnet nach UTC, und in
//    MESZ (UTC+2) wird aus Montag 00:00 Ortszeit der Sonntag 22:00 UTC -
//    `toISOString().slice(0,10)` gab damit den 18.10. statt des 19.10. zurück.
//    Die Folge war im Screenshot zu sehen: der Schlüssel passte zu keiner Woche
//    des Endpunkts, also entstand eine zweite, leere Zeile neben der echten
//    (Georg 2026-10-21: "der lebenszyklus ist drin aber die Werte der Woche
//    fehlen"). Hier wird das Datum aus den LOKALEN Feldern gebaut.
function kpiWoche(datum) {
    const d = new Date(datum + 'T00:00:00');
    const wt = (d.getDay() + 2) % 7;             // Fr=0 … Do=6
    const fr = new Date(d); fr.setDate(d.getDate() - wt);
    const dn = new Date(fr); dn.setDate(fr.getDate() + 6);
    const f = x => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
    return { von: f(fr), bis: f(dn) };
}

/**
 * Wer an einem Lebenszyklus-Satz arbeitet, in Kurzform fuer den Chip.
 *
 * 🔴 ZWEI FELDER, EINE ANTWORT (Georg 2026-10-22). `schritt` traegt den Zustand
 *    samt Agentennummer (`in_arbeit_agent2`), `nutzer` den, der den Satz
 *    angelegt hat. Steht beides, gewinnt `schritt` - er ist der juengere Stand.
 *    Keins von beiden: der Satz ist aus der Zeit vor dem Feld, und dann steht
 *    da nichts statt einer erfundenen Zuordnung.
 */
function bearbeiter(h) {
    const s = h.schritt || '';
    const m = s.match(/^(in_arbeit|umgesetzt)_agent(\d+)$/);
    if (m) return (m[1] === 'in_arbeit' ? '🛠 A' : '✓ A') + m[2];
    if (s === 'in_arbeit') return '🛠 ich';
    if (s === 'vorbereitet') return '📋 bereit';
    if (s === 'umgesetzt') return '✓ ich';
    if (h.nutzer && h.nutzer !== 'claude') return h.nutzer;
    // 🔴 LEER IST AUCH EIN ZUSTAND, und er wird GEZEIGT (Georg 2026-10-22:
    //    "Wo ist das Statusfeld?"). Vorher gab die Funktion hier '' zurück und
    //    das Feld verschwand - von außen nicht zu unterscheiden davon, dass die
    //    Anzeige fehlt. Alle 184 Sätze tragen heute `schritt = NULL`, weil noch
    //    kein Agent sich angemeldet hat; genau das soll man sehen können.
    return 'kein Status';
}


function Wochendaten({ page, token, onNeu, offen, aufklappen, stand, gemessen }) {
    const [satzOffen, setSatzOffen] = useState(null);
    const hist = page.hist || [];

    // 🔴 WOCHEN OHNE DATEN WERDEN ERGAENZT (Georg 2026-10-21: "den Lebenszyklus
    //    an den wochensatz haengen wo das h-datum liegt"). Der Grund steht in
    //    seinem Screenshot: Die Handlung war am 21.10., die Tagesdaten enden am
    //    20.10. - GSC hinkt rund drei Tage hinterher. Ohne diese Ergaenzung
    //    faellt JEDE frische Handlung aus der Tabelle, und zwar genau die, die
    //    man sehen will.
    //    Die erzeugte Zeile traegt keine Zahlen, nur den Prozess.
    const wochen = useMemo(() => {
        const vorhanden = new Map((page.wochen || []).map(w => [w.von, w]));
        hist.forEach(h => {
            if (!h.h_datum) return;
            const { von, bis } = kpiWoche(h.h_datum);
            if (!vorhanden.has(von)) {
                vorhanden.set(von, { von, bis, impr: null, klicks: null,
                                     ctr: null, pos: null, tage: 0, ohne_daten: true });
            }
        });
        // 🔴 EINE WOCHE OHNE IMPRESSION FEHLTE (Georg 2026-11-02, LB06: "es fehlt eine
        //    Woche"). Die Search Console liefert fuer einen Tag ohne Impression KEINE
        //    Zeile, keine 0 - die Woche 23.-29.10. sah aus wie eine Datenluecke und war
        //    der Befund. Aufgefuellt wird von der aeltesten Woche bis zur Woche des
        //    juengsten Datentags ALLER Seiten (`summary.latest_day`): bis dorthin ist
        //    gemessen, also heisst keine Zeile 0. Danach bleibt "keine Tagesdaten" -
        //    eine Woche, die Google noch nicht geliefert hat, ist nicht null.
        // 🔴 Und nur Wochen, in denen IRGENDEINE Seite Daten hat (`gemessen`) - sonst liest das
        //    Panel einen ausgefallenen Abruf als Null (Regel: Eine Seite ohne genug Nachfrage
        //    liefert keine Null, sondern keine Zahl - "eine Lücke im Ingest als Null gelesen").
        if (stand && vorhanden.size) {
            const bisVon = kpiWoche(stand).von;
            let { von } = kpiWoche([...vorhanden.keys()].sort()[0]);
            while (von <= bisVon) {
                const w = kpiWoche(von);
                const alt = vorhanden.get(w.von);
                if ((!alt || alt.ohne_daten) && (!gemessen || gemessen.has(w.von))) {
                    vorhanden.set(w.von, { ...w, impr: 0, klicks: 0, ctr: null, pos: null,
                                           tage: 0, null_woche: true });
                }
                const n = new Date(w.von + 'T00:00:00'); n.setDate(n.getDate() + 7);
                von = kpiWoche(`${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`).von;
            }
        }
        return [...vorhanden.values()].sort((a, b) => b.von.localeCompare(a.von));
    }, [page.wochen, hist, stand, gemessen]);

    // Jeden Hist-Eintrag seiner Woche zuordnen - jetzt faellt keiner mehr raus.
    const histDerWoche = w => hist.filter(h => h.h_datum >= w.von && h.h_datum <= w.bis);

    const key = w => `${w.von}|${w.bis}`;

    // ⚠ Hier stand bis zum 2026-10-22 der CSV-Export dieser Tabelle samt
    //   Zeilenauswahl (Georg: "die Zeile csv Export kann ganz raus"). Mit ihm
    //   fiel die Checkbox-Spalte weg - sie hatte keinen anderen Zweck.
    //   Die Status-CSV und die Queries-CSV im Bedienfeld oben bleiben; sie
    //   gehen über alle Seiten und sind etwas anderes als diese eine Tabelle.

    return (
        <div className="gs-wochen">
            <div className="gs-wochen-kopf" onClick={aufklappen} role="button" tabIndex={0}
                 onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); aufklappen(); } }}>
                <span className="gs-panel-caret">{offen ? '▾' : '▸'}</span>
                <strong>Wochendaten</strong>
                <span className="gs-wochen-hinweis">
                    {wochen.length
                        ? `${wochen.length} Woche${wochen.length === 1 ? '' : 'n'} · ${wochen[0].bis} zurück`
                        : 'keine Tagesdaten für diese Seite'}
                </span>
                <span className="gs-wochen-marke">
                    {hist.length > 0 ? `Lebenszyklus: ${hist.length}` : 'kein Lebenszyklus'}
                </span>
            </div>

            {offen && (
                <div className="gs-wochen-flaeche">
                    {wochen.length === 0 ? (
                        <p className="gs-wochen-leer">
                            Für diese Seite stehen in <code>gsc_daily_data</code> keine Tage
                            der letzten 26 Wochen — das ist bei Seiten ohne Impressionen normal.
                        </p>
                    ) : (
                        <>
                            {/* ⚠ Die CSV-Leiste ist am 2026-10-22 entfallen (Georg: "die
                                Zeile csv Export kann ganz raus"). Mit ihr fiel die
                                Checkbox-Spalte weg - sie hatte keinen anderen Zweck als
                                die Zeilenauswahl für den Export. Die Status-CSV und die
                                Queries-CSV oben im Bedienfeld bleiben. */}
                            <div className="gs-wochen-tabwrap">
                            <table className="gs-wochen-tab">
                                <thead><tr>
                                    <th title="Handlung · Prognose · Prüftag · wer daran arbeitet">Prozess</th>
                                    <th>Typ</th><th>Zeitraum</th>
                                    <th className="r" title="Tage mit Daten in dieser Woche">T</th>
                                    <th className="r">Impr.</th><th className="r">Klicks</th>
                                    <th className="r">CTR</th><th className="r">Ø Pos.</th>
                                </tr></thead>
                                <tbody>
                                    {wochen.map(w => {
                                        const hs = histDerWoche(w);
                                        // 🔴 EINE ZEILE JE SATZ (Georg 2026-10-22: "Du musst die Details
                                        //    gleich unter dem Eintrag öffnen zu dem sie gehören"). Vorher
                                        //    standen alle Chips in EINER Zelle und das Detail darunter -
                                        //    bei fünf Sätzen öffnete es fünf Chips tiefer, und bei einer
                                        //    längeren Liste war es gar nicht mehr im Bild.
                                        //    Die Wochenwerte stehen nur in der ERSTEN Zeile; die Folgezeilen
                                        //    lassen sie leer, statt sie zu wiederholen. Ein `rowSpan` käme
                                        //    nicht in Frage: die Zeilenzahl ändert sich beim Aufklappen.
                                        const zeilen = hs.length ? hs : [null];
                                        return (
                                            <React.Fragment key={key(w)}>
                                            {zeilen.map((h, i) => (
                                                <React.Fragment key={h ? h.id : 'leer'}>
                                                <tr className={(w.ohne_daten ? 'gs-w-leer' : '')
                                                               + (i > 0 ? ' gs-w-folge' : '')}>
                                                    {/* Der Knopf trägt H-Datum und P-Datum (Georg 2026-10-21):
                                                        erst ihr ABSTAND macht die Zeile deutbar - was wann getan
                                                        wurde und wann es sich zeigen muss. */}
                                                    <td className="gs-w-prozess">
                                                        {h && (
                                                            <button className={'gs-w-chip'
                                                                        + (h.faellig ? ' gs-w-chip-faellig'
                                                                           : h.eingetreten === null ? ' gs-w-chip-offen' : '')
                                                                        + (h.id === satzOffen ? ' gs-w-chip-auf' : '')}
                                                                    onClick={() => setSatzOffen(satzOffen === h.id ? null : h.id)}
                                                                    title={`${h.arbeit || ''}\n${h.handlung}`}>
                                                                {h.eingetreten === 1 ? '✅' : h.eingetreten === 0 ? '🔴' : h.faellig ? '🔴' : '⏳'}
                                                                {' '}<strong>H {fmtTag(h.h_datum)}</strong>
                                                                {h.pruef_datum && <em> · P {fmtTag(h.pruef_datum)}</em>}
                                                                {bearbeiter(h) && (
                                                                    <span className={'gs-w-wer' + (/^in_arbeit/.test(h.schritt || '') ? ' gs-w-wer-aktiv' : '')}
                                                                          title={`${h.schritt || ''}${h.updated_at ? ' · seit ' + fmtTs(h.updated_at) : ''}`}>
                                                                        {bearbeiter(h)}
                                                                    </span>
                                                                )}
                                                                <span className="gs-w-chip-caret">{h.id === satzOffen ? '▾' : '▸'}</span>
                                                            </button>
                                                        )}
                                                    </td>
                                                    {i === 0 ? (
                                                        <>
                                                        <td><span className="gs-w-typ">Woche</span></td>
                                                        <td className="gs-w-zeit"
                                                            title={w.ohne_daten ? 'keine Tagesdaten in dieser Woche'
                                                                   : w.null_woche ? `keine Impression - gemessen bis ${stand}, die Search Console liefert für 0 Impressionen keine Zeile`
                                                                   : `gemessen ${w.daten_von} bis ${w.daten_bis}`}>
                                                            {fmtTag(w.von)}–{fmtTag(w.bis)}{String(w.bis).slice(0, 4)}
                                                        </td>
                                                        {/* T = Tage MIT Daten. Eine angebrochene Woche mit zwei
                                                            Tagen ist mit einer vollen nicht vergleichbar. */}
                                                        <td className="r">{w.ohne_daten ? '–' : w.tage}</td>
                                                        <td className="r">{w.impr == null ? '–' : w.impr}</td>
                                                        <td className="r">{w.klicks == null ? '–' : w.klicks}</td>
                                                        <td className="r">{w.ctr != null ? String(w.ctr).replace('.', ',') + ' %' : '–'}</td>
                                                        <td className="r">{w.pos != null ? String(w.pos).replace('.', ',') : '–'}</td>
                                                        </>
                                                    ) : (
                                                        <td colSpan={7} className="gs-w-fortsetzung"></td>
                                                    )}
                                                </tr>
                                                {/* 🔴 Das Detail steht DIREKT unter SEINEM Eintrag */}
                                                {h && h.id === satzOffen && (
                                                    <tr className="gs-w-satzzeile">
                                                        <td colSpan={8}>
                                                            <Lebenslauf page={{ ...page, hist: [h] }}
                                                                        token={token} onNeu={onNeu} startOffen />
                                                        </td>
                                                    </tr>
                                                )}
                                                </React.Fragment>
                                            ))}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                            </div>
                        </>
                    )}

                    {/* ⚠ Der frühere Block "außerhalb des Zeitraums" ist entfallen: seit
                        die fehlende Woche erzeugt wird, hat JEDER Eintrag seine Zeile.
                        Ein zweiter Ort für dieselbe Sache wäre eine zweite Wahrheit. */}
                </div>
            )}
        </div>
    );
}


/**
 * Ein PANEL je Seite (Georg 2026-10-21: "Jede Seite ist ein Panel wie in GADS KPI
 * Performance Dashboard die Bxx Panels").
 *
 * 🔴 DREI EBENEN, UND JEDE OEFFNET DIE NAECHSTE:
 *    1. Der Kopf traegt die Kennzahlen und ist immer sichtbar - Kennung,
 *       Sparkline, Pillar, Index, Crawl, Rank, Impressionen, Klicks.
 *    2. Aufgeklappt zeigt das Panel die WOCHENDATEN der Seite.
 *    3. Klappt man eine Woche auf, erscheint der LEBENSZYKLUS dieser Seite.
 *
 * ⚠ Der Kopf ist ein DIV, kein BUTTON. Er enthaelt den Crawl-Chip und den
 *   Link zur Seite, und beide sind selbst klickbar - ein Button im Button ist
 *   ungueltiges HTML, und der Browser wuerde den inneren Klick verschlucken.
 *   Darum traegt jedes innere Element `stopPropagation`.
 */
function SeitenPanel({ page, token, offen, aufklappen, onNeu, stand, gemessen }) {
    const [wochenAuf, setWochenAuf] = useState(false);
    const p = page;
    const hist = p.hist || [];
    const fällig = hist.filter(h => h.faellig).length;
    const offenN = hist.filter(h => h.eingetreten === null).length;
    const halt = e => e.stopPropagation();

    return (
        <section className={`gs-panel gs-panel-${p.problem_level}`}>
            <div className="gs-panel-head" onClick={aufklappen}
                 role="button" tabIndex={0} aria-expanded={offen}
                 onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); aufklappen(); } }}>
                <span className="gs-panel-caret">{offen ? '▾' : '▸'}</span>
                <span className="gs-panel-seite">
                    <strong>{p.prefix}</strong>
                    <a href={p.url} target="_blank" rel="noopener noreferrer"
                       className="gs-link" title={p.url} onClick={halt}>↗</a>
                </span>
                <span className="gs-panel-spark"><Sparkline values={p.spark} /></span>
                <span className="gs-panel-pillar">
                    {p.pillar ? <span className="gs-pillar" style={{ background: PILLAR_COLORS[p.pillar] || '#888' }}>{p.pillar}</span> : ''}
                </span>
                <span className="gs-panel-index"><IndexBadge p={p} /></span>
                <span className="gs-panel-crawl" onClick={halt}><CrawlHistory page={p} /></span>
                <span className="gs-panel-rank" title="Ø-Position">
                    {p.rank !== null ? String(p.rank).replace('.', ',') : '–'}
                </span>
                <span className="gs-panel-zahl" title="Impressionen">{p.impressions}</span>
                <span className="gs-panel-zahl" title="Klicks">{p.clicks}</span>
                <span className="gs-panel-marken">
                    {/* 🔴 Wer gerade an dieser Seite arbeitet - ohne Aufklappen sichtbar
                        (Georg 2026-10-22). Nur `in_arbeit*`, denn nur das ist eine Sperre:
                        eine Seite, die jemand in Arbeit hat, nimmt kein zweiter. */}
                    {(() => {
                        const a = hist.find(h => /^in_arbeit/.test(h.schritt || ''));
                        return a ? <span className="gs-marke gs-marke-arbeit"
                                         title={`${a.schritt}${a.updated_at ? ' · seit ' + fmtTs(a.updated_at) : ''}`}>
                                       {bearbeiter(a)}
                                   </span> : null;
                    })()}
                    {fällig > 0 && <span className="gs-marke gs-marke-faellig" title="fällige Prüfungen">⏰ {fällig}</span>}
                    {offenN > 0 && <span className="gs-marke gs-marke-offen" title="offene Prognosen">{offenN} offen</span>}
                    {hist.length > 0 && <span className="gs-marke" title="Einträge im Lebenslauf">📋 {hist.length}</span>}
                    {(p.notes || []).length > 0 && <span className="gs-marke" title="Notizen">📝 {p.notes.length}</span>}
                </span>
            </div>

            {offen && (
                <div className="gs-panel-body">
                    {/* Ebene 2: die Wochendaten der Seite. 🔴 Der Endpunkt liefert sie
                        noch NICHT (gsc_get_status.php kennt spark, hist, crawls, notes).
                        Der Platz steht, damit die Ebenen stimmen, sobald sie kommen -
                        Georg 2026-10-21: "und dann folgen in Zukunft noch die Wochendaten". */}
                    <Wochendaten page={p} token={token} onNeu={onNeu} stand={stand} gemessen={gemessen}
                                 offen={wochenAuf} aufklappen={() => setWochenAuf(v => !v)} />

                    <div className="gs-panel-notizen">
                        <NotesLog page={p} token={token} />
                    </div>
                </div>
            )}
        </section>
    );
}


function NotesLog({ page, token }) {
    const problemText = (page.problems || []).join(' · ');
    const [notes, setNotes] = useState(page.notes || []);
    const [draft, setDraft] = useState((page.notes && page.notes.length) ? '' : problemText);
    const [busy, setBusy] = useState(false);

    async function add() {
        const text = draft.trim();
        if (!text || busy) return;
        setBusy(true);
        const source = (text === problemText.trim() && problemText) ? 'problem' : 'user';
        try {
            const res = await fetch(`${API_BASE}/gsc_notes_log.php`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ url: page.url, prefix: page.prefix, note: text, source }),
            });
            const j = await res.json();
            if (j.success) { setNotes([{ id: j.id, note: text, source: j.source || source, created_at: j.created_at }, ...notes]); setDraft(''); }
        } catch (e) { console.error('Notiz speichern:', e); }
        setBusy(false);
    }

    async function del(id) {
        try {
            const res = await fetch(`${API_BASE}/gsc_notes_log.php?id=${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
            const j = await res.json();
            if (j.success) setNotes(notes.filter(n => n.id !== id));
        } catch (e) { console.error('Notiz löschen:', e); }
    }

    return (
        <div className="gs-notes">
            <div className="gs-notes-input">
                <textarea value={draft} rows={2} placeholder="Notiz…" className="gs-notes-ta"
                    onChange={e => setDraft(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); add(); } }} />
                <button className="gs-notes-add" onClick={add} disabled={busy || !draft.trim()} title="Notiz speichern (⌘/Strg+Enter)">＋</button>
            </div>
            {notes.length > 0 && (
                <ul className="gs-notes-list">
                    {notes.map(n => (
                        <li key={n.id} className={n.source === 'problem' ? 'gs-note-problem' : ''}>
                            <span className="gs-note-ts">{fmtTs(n.created_at)}</span>
                            <span className="gs-note-txt">{n.source === 'problem' ? '🔴 ' : ''}{n.note}</span>
                            <button className="gs-note-del" onClick={() => del(n.id)} title="Eintrag löschen">×</button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

export default function GscStatus() {
    const token = localStorage.getItem("token");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [onlyProblems, setOnlyProblems] = useState(false);
    const [days, setDays] = useState(7);
    const [sort, setSort] = useState(null);   // null = Vorgabe | { key, dir }
    // 🔴 Zähler statt eigener Ladefunktion: ein neuer Lebenslauf-Eintrag erhöht ihn,
    //    der vorhandene Effekt lädt daraufhin neu. So bleibt EIN Ladeweg bestehen.
    const [neuladen, setNeuladen] = useState(0);
    // Welche Panels sind offen — Schlüssel ist prefix+url, weil eine Kennung
    // theoretisch zweimal vorkommen kann (LP55 ≠ LB55, und ein Slug-Wechsel
    // hinterlässt kurzzeitig zwei Zeilen).
    const [panelOffen, setPanelOffen] = useState({});
    const [suche, setSuche] = useState('');
    // Wochen, in denen IRGENDEINE Seite Daten hat - nur sie füllt Wochendaten mit 0 auf.
    const gemessen = useMemo(() => (data && data.pages)
        ? new Set(data.pages.flatMap(p => (p.wochen || []).map(w => w.von))) : null, [data]);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        fetch(`${API_BASE}/gsc_get_status.php?days=${days}`, { headers: { "Authorization": `Bearer ${token}` } })
            .then(r => r.json())
            .then(j => { if (alive) { setData(j); setLoading(false); } })
            .catch(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [days, token, neuladen]);

    // CSV-Export für Claude Desktop (gsc_export_csv.php, grain status|queries) via Blob+Bearer.
    const downloadCsv = async (grain) => {
        try {
            const res = await fetch(`${API_BASE}/gsc_export_csv.php?grain=${grain}`, { headers: { "Authorization": `Bearer ${token}` } });
            if (!res.ok) { console.error("CSV HTTP", res.status); return; }
            const blob = await res.blob();
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.download = `gsc_${grain}.csv`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(a.href);
        } catch (e) { console.error("CSV-Export-Fehler:", e); }
    };

    // Jede Spalte sortierbar. Klick zyklisch: Vorgabe → 1. Richtung → Gegenrichtung
    // → Vorgabe. Die Vorgabe (Probleme zuerst, dann Impressionen) kommt vom
    // Endpunkt und bleibt erreichbar — sie ist die Arbeitsreihenfolge.
    // `erst` = die Richtung, die beim ersten Klick sinnvoll ist: bei Zahlen die
    // große zuerst, bei Crawl und Rank die schlechte zuerst (älteste / hinterste).
    const shown = useMemo(() => {
        // 🔴 Die Suche trifft Kennung UND URL (Georg 2026-10-21: "bau eine Suche
        //    nach einer Seite ein z.B.: LPxx"). Beides, weil man eine Seite mal
        //    über „LP28" und mal über ein Wort aus dem Permalink sucht.
        //    Kleinschreibung auf beiden Seiten - "lp28" muss LP28 finden.
        //    Regel: [[Jede Zeichenkette wird kleingeschrieben, bevor ihre Bedeutung gerechnet wird]]
        const q = suche.trim().toLowerCase();
        const liste = (data?.pages || [])
            .filter(p => !onlyProblems || p.problem_level !== 'ok')
            .filter(p => !q
                || (p.prefix || '').toLowerCase().includes(q)
                || (p.url || '').toLowerCase().includes(q));
        if (!sort) return liste;
        const wert = SPALTEN[sort.key]?.wert;
        if (!wert) return liste;
        const vz = sort.dir === 'asc' ? 1 : -1;
        return [...liste].sort((a, b) => {
            const x = wert(a), y = wert(b);
            if (typeof x === 'string' || typeof y === 'string') {
                return vz * String(x).localeCompare(String(y), 'de');
            }
            return vz * (x - y);
        });
    }, [data, onlyProblems, sort, suche]);

    const klickSpalte = key => {
        const erst = SPALTEN[key].erst;
        setSort(s0 => !s0 || s0.key !== key ? { key, dir: erst }
                    : s0.dir === erst ? { key, dir: erst === 'asc' ? 'desc' : 'asc' }
                    : null);
    };
    // Tooltip bewusst KURZ (eine Zeile): ein langer title-Kasten legt sich über
    // mehrere Zeilen und macht sie unlesbar (Georg 2026-08-22).
    // Die ausführliche Erklärung steht in der Fußzeile, nicht im Tooltip.
    const SortKnopf = ({ id, title, children }) => (
        <button className={'gs-sortknopf' + (sort?.key === id ? ' gs-sortknopf-aktiv' : '')}
                onClick={() => klickSpalte(id)} title={title}>
            {children}{' '}
            <span className="gs-sort-pfeil">
                {sort?.key !== id ? '⇅' : sort.dir === 'asc' ? '▲' : '▼'}
            </span>
        </button>
    );

    const s = data?.summary || {};

    return (
        <div className="gs-wrap">
            <Link to="/admin" className="gs-back">← Zurück zum Dashboard</Link>
            <Link to="/gsc-wochenbericht" className="gs-back">📝 Wochenbericht</Link>
            <h2>🔎 GSC Seiten-Status <small>— wo steht jede LP/LB, Rank &amp; Probleme</small></h2>

            {loading ? <p>lädt…</p> : !data?.success ? <p>Fehler beim Laden.</p> : (
            <>
                <div className="gs-summary">
                    <span className="gs-pill">{s.total} Seiten</span>
                    <span className="gs-pill gs-b-ok">✅ {s.indexed} indexiert</span>
                    <span className="gs-pill gs-b-crit">🔴 {s.critical} kritisch</span>
                    <span className="gs-pill gs-b-warn">🟡 {s.warn} Warnung</span>
                    <span className="gs-pill gs-note">Rank = Ø-Position der letzten {s.window_days} Tage (bis {s.latest_day}) · niedriger = besser</span>
                </div>

                <div className="gs-controls">
                    {/* 🔴 Suche nach einer Seite (Georg 2026-10-21). Sie steht VOR
                        dem Problemfilter, weil sie der häufigere Griff ist: man
                        sucht eine bestimmte Seite, nicht eine Menge. */}
                    <span className="gs-suche">
                        <input type="search" value={suche} placeholder="Seite suchen, z. B. LP28"
                               onChange={e => setSuche(e.target.value)}
                               aria-label="Seite suchen — Kennung oder Teil der URL" />
                        {suche && (
                            <button className="gs-suche-x" onClick={() => setSuche('')}
                                    title="Suche zurücksetzen">✕</button>
                        )}
                    </span>
                    <label><input type="checkbox" checked={onlyProblems} onChange={e => setOnlyProblems(e.target.checked)} /> nur Probleme zeigen</label>
                    <span className="gs-days">Fenster:&nbsp;
                        {[7, 14, 28].map(d => (
                            <button key={d} className={days === d ? 'active' : ''} onClick={() => setDays(d)}>{d}T</button>
                        ))}
                    </span>
                    <span className="gs-count">
                        {shown.length} angezeigt
                        {sort && (
                            <button className="gs-sort-reset" onClick={() => setSort(null)}
                                title="Zurück zur Vorgabe: Probleme zuerst">
                                sortiert: {SPALTEN[sort.key].label} {sort.dir === 'asc' ? '▲' : '▼'} ✕
                            </button>
                        )}
                    </span>
                    <span style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
                        <button onClick={() => downloadCsv('status')} title="Status-CSV: 1 Zeile je Seite (Index-Status + Problem-Gruppe + Performance). Für Excel/Claude Desktop."
                                style={{ padding: '4px 10px', borderRadius: '5px', border: '1px solid #1a7f5a', background: '#eafaf3', color: '#1a7f5a', cursor: 'pointer', fontSize: '13px' }}>⬇ Status-CSV</button>
                        <button onClick={() => downloadCsv('queries')} title="Queries-CSV: je (Seite × Suchbegriff) — für Content-/Kannibalisierungs-Analyse."
                                style={{ padding: '4px 10px', borderRadius: '5px', border: '1px solid #1a5a7f', background: '#eaf3fa', color: '#1a5a7f', cursor: 'pointer', fontSize: '13px' }}>⬇ Queries-CSV</button>
                    </span>
                </div>

                {/* 🔴 Die Spaltenkoepfe bleiben als SORTIERLEISTE stehen, obwohl es
                    keine Tabelle mehr ist. Sie sind der einzige Weg, die Reihenfolge
                    zu aendern, und ihre Beschriftung sagt zugleich, was im Panelkopf
                    an welcher Stelle steht. */}
                <div className="gs-sortleiste">
                    <span className="gs-sortleiste-text">sortieren nach</span>
                    <SortKnopf id="prefix" title="LP-/LB-Kennung">Seite</SortKnopf>
                    <SortKnopf id="spark"  title="Impressionen der letzten 30 Tage">Impr. 30T</SortKnopf>
                    <SortKnopf id="pillar" title="Themen-Säule">Pillar</SortKnopf>
                    <SortKnopf id="index"  title="nicht indexiert zuerst">Index</SortKnopf>
                    <SortKnopf id="crawl"  title="ältester Crawl zuerst">🕷 Crawl</SortKnopf>
                    <SortKnopf id="rank"   title="hinterste Position zuerst">Rank</SortKnopf>
                    <SortKnopf id="impr"   title="meiste Impressionen zuerst">Impr</SortKnopf>
                    <SortKnopf id="klicks" title="meiste Klicks zuerst">Klicks</SortKnopf>
                    <SortKnopf id="hist"   title="fällige Prüfungen zuerst">📋 Lebenslauf</SortKnopf>
                    <SortKnopf id="notes"  title="meiste Notizen zuerst">📝 Notizen</SortKnopf>
                </div>

                <div className="gs-panels">
                    {shown.length === 0 ? (
                        <p className="gs-leer">
                            Keine Seite passt{suche.trim() ? <> zu „<strong>{suche.trim()}</strong>"</> : ''}
                            {onlyProblems ? ' und dem Filter „nur Probleme"' : ''}.
                        </p>
                    ) : shown.map(p => (
                        <SeitenPanel
                            key={p.prefix + p.url}
                            page={p}
                            token={token}
                            offen={!!panelOffen[p.prefix + p.url]}
                            aufklappen={() => setPanelOffen(o => ({ ...o, [p.prefix + p.url]: !o[p.prefix + p.url] }))}
                            onNeu={() => setNeuladen(n => n + 1)}
                            stand={data && data.summary ? data.summary.latest_day : null}
                            gemessen={gemessen}
                        />
                    ))}
                </div>
                <p className="gs-foot">Datenquellen: <code>gsc_url_inspection</code> (Index/Crawl, tägl. 04:10) + <code>gsc_crawl_history</code> (Crawl-Verlauf, direkt nach dem Inspect — jeder neue Zeitpunkt, nichts wird überschrieben) + <code>gsc_daily_data</code> (Rank/Impr, tägl. 04:00). <b>🕷 Crawl</b> = letzter Googlebot-Besuch, Zahl dahinter = Alter in Tagen, <code>n×</code> = erfasste Zeitpunkte; klicken öffnet den Verlauf. <b>🔗n</b> = verweisende Seiten laut Google zum Crawl-Zeitpunkt (<b>rot bei 0</b> — auf diese Seite zeigt kein bekannter Link); Google liefert dazu eine <b>Stichprobe</b>, keine vollständige Liste. <b>Google kennt kein „Indexierungsdatum"</b> — die Crawl-Zeit ist die einzige echte Zeitangabe je URL. Der Verlauf sammelt ab 2026-08-22, rückwirkend gibt es ihn nicht. <b>„Impr. 30T"</b> = Impressionen je Tag der rollierenden letzten 30 Tage (grün); „–" = keine Impressionen (bei dem Volumen normal). Rank-Deltas &amp; „0 Impr." sind bei dem Volumen bewusst KEINE Flags (Rauschen) — belastbar sind Index-/Crawl-Signale.</p>
            </>
            )}
        </div>
    );
}
